import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UpsellSettingsPanel = ({ authHeaders }) => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  // eslint-disable-next-line
  useEffect(() => { axios.get(`${API}/admin/upsell-settings`, authHeaders).then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/upsell-settings`, { ...s, discount_aud: Number(s.discount_aud), hours: Number(s.hours) }, authHeaders);
      setS(r.data); toast.success('Thank-you offer settings saved');
    } catch { toast.error('Discount must be A$0–100 and the time limit 1–72 hours'); }
    setSaving(false);
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6" data-testid="upsell-settings-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-lg font-semibold text-[#1E1622]">Thank-you offer settings</h3>
        <label className="inline-flex items-center gap-2 text-sm text-[#1E1622]/70 cursor-pointer">
          <input type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} data-testid="upsell-enabled-toggle" className="w-4 h-4 accent-[#3B164C]" />
          {s.enabled ? 'On' : 'Off'}
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-[#1E1622] mb-2">Extra discount (A$)</label>
          <input type="number" min="0" max="100" step="0.5" value={s.discount_aud} onChange={(e) => setS({ ...s, discount_aud: e.target.value })} data-testid="upsell-discount-input"
            className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#1E1622] mb-2">Time limit (hours, 1–72)</label>
          <input type="number" min="1" max="72" value={s.hours} onChange={(e) => setS({ ...s, hours: e.target.value })} data-testid="upsell-hours-input"
            className="w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none text-sm" />
        </div>
      </div>
      <p className="text-xs text-[#1E1622]/50 mb-4">
        {s.enabled ? `Single-item buyers see the Bundle for their credit price minus A$${s.discount_aud || 0}, for ${s.hours || 0} hours after purchase.` : 'The thank-you page offer is hidden. Reminder emails still go out as normal.'}
      </p>
      <button onClick={save} disabled={saving} data-testid="upsell-settings-save" className="btn-primary text-sm inline-flex items-center gap-2">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
      </button>
    </div>
  );
};

const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); };

export const MonthlyReportPanel = ({ authHeaders }) => {
  const [month, setMonth] = useState(lastMonth());
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    setPreview(null);
    axios.get(`${API}/admin/monthly-report`, { ...authHeaders, params: { month } }).then((r) => setPreview(r.data)).catch(() => toast.error('Could not load the report'));
    // eslint-disable-next-line
  }, [month]);
  const send = async () => {
    setSending(true);
    try { const r = await axios.post(`${API}/admin/monthly-report/send`, null, { ...authHeaders, params: { month } }); toast.success(`Report sent to ${r.data.recipient}`); }
    catch { toast.error('Could not send the report'); }
    setSending(false);
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6" data-testid="monthly-report-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h3 className="font-serif text-lg font-semibold text-[#1E1622]">Monthly results email</h3>
        <div className="flex items-center gap-3">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="monthly-report-month" className="p-2 border border-[#E6DAC5] rounded-lg text-sm" />
          <button onClick={send} disabled={sending || !preview} data-testid="monthly-report-send" className="btn-secondary text-sm inline-flex items-center gap-2">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send me this report
          </button>
        </div>
      </div>
      <p className="text-xs text-[#1E1622]/50 mb-4">Sent automatically to {preview?.recipient || 'the owner'} at 8am Sydney time on the 1st of each month, covering the month before.</p>
      {preview ? <iframe title="Monthly report preview" srcDoc={preview.html} sandbox="" className="w-full h-[560px] border border-[#E6DAC5] rounded-xl" data-testid="monthly-report-preview" />
        : <Loader2 className="animate-spin text-[#3B164C]" />}
    </div>
  );
};
